import "react-native-get-random-values";
import "react-native-reanimated";
import DrawerContent from "@/components/layout/DrawerContent";
import Header from "@/components/layout/Header";
import { AuthProvider } from "@/contexts/AuthContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Drawer } from "expo-router/drawer";
import { useCallback, useState } from "react";
import { Platform } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import "./../../global.css";
import { Manifest } from "@/components/layout/Manifest";
import { CustomThemeProvider } from "@/contexts/CustomThemeContext";
import { useStoredNavigationState } from "@/hooks/useStoredNavigationState";
import type { DrawerNavigationState, ParamListBase } from "@react-navigation/native";
import * as Sentry from "@sentry/react-native";
import Constants, { ExecutionEnvironment } from "expo-constants";
import { useFocusEffect } from "expo-router";
import { OrientationLock, lockAsync, supportsOrientationLockAsync } from "expo-screen-orientation";
import { StatusBar } from "expo-status-bar";
import { SafeAreaProvider } from "react-native-safe-area-context";

const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: Constants.executionEnvironment === ExecutionEnvironment.StoreClient, // Only in native builds, not in Expo Go.
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 0,
    },
  },
});

function shouldResetHistory(latestRoute: string) {
  return ["index", "purchase/confirmation/index", "qr/index", "redeem/confirmation/index", "welcome/index"].includes(latestRoute);
}

function RootLayout() {
  const [mode] = useState<"light" | "dark" | "system">("system");
  const { canLoadStored, cleanHistory, cleanStored, loadStored, saveStored, validateStored } = useStoredNavigationState();

  useFocusEffect(
    useCallback(() => {
      // Lock only to portrait mode.
      supportsOrientationLockAsync(OrientationLock.PORTRAIT_UP)
        .then((): Promise<void> => lockAsync(OrientationLock.PORTRAIT_UP))
        .catch((error): void => {
          if (error.name !== "NotSupportedError") {
            console.warn("Could not lock to portrait mode: ", error);
          }
        });
    }, []),
  );

  const drawDrawerContent = () => <DrawerContent />;

  const handleScreenNavigationStateChange = useCallback(
    async (state: DrawerNavigationState<ParamListBase>) => {
      const history = state.history;
      const latestRoute = state.routeNames[state.index];

      // Used to show the drawer
      if (history[history.length - 1].type === "drawer") {
        return;
      }

      // Reset the history on required screens
      if (history.length !== 1 && shouldResetHistory(latestRoute)) {
        if (Platform.OS === "web") {
          await cleanStored();
        }
        await cleanHistory(state);
        return;
      }

      if (Platform.OS === "web") {
        // Clean the current stored state if there is
        // a new version of the app running
        await validateStored();

        // Load history if there is no history yet (page refresh)
        if (history.length === 1 && !shouldResetHistory(latestRoute) && (await canLoadStored(latestRoute))) {
          await loadStored();
        } else {
          await saveStored(state);
        }
      }
    },
    [canLoadStored, cleanHistory, cleanStored, loadStored, saveStored, validateStored],
  );

  return (
    <SafeAreaProvider>
      <StatusBar style="dark" />
      <GestureHandlerRootView className="flex-1 overflow-hidden">
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CustomThemeProvider mode={mode}>
              <Manifest />
              <Header />
              <Drawer
                backBehavior="history"
                defaultStatus="closed"
                drawerContent={drawDrawerContent}
                screenListeners={{
                  state: (event) => {
                    handleScreenNavigationStateChange(event.data.state);
                  },
                }}
                screenOptions={{
                  drawerPosition: "right",
                  headerShown: false,
                }}
              />
            </CustomThemeProvider>
          </AuthProvider>
        </QueryClientProvider>
      </GestureHandlerRootView>
    </SafeAreaProvider>
  );
}

if (process.env.EXPO_PUBLIC_SENTRY_DNS) {
  Sentry.init({
    dsn: process.env.EXPO_PUBLIC_SENTRY_DNS,
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for tracing.
    // We recommend adjusting this value in production.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: __DEV__ ? 1 : 0.1,
    integrations: [
      navigationIntegration,
      Sentry.mobileReplayIntegration({
        maskAllText: true,
        maskAllImages: false,
        maskAllVectors: false,
      }),
    ],
    replaysSessionSampleRate: __DEV__ ? 1 : 0.1, // 10% in production, 100% otherwise
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    enableNativeFramesTracking: Constants.executionEnvironment === ExecutionEnvironment.StoreClient, // Only in native builds, not in Expo Go.
    environment: process.env.EXPO_PUBLIC_ENVIRONMENT,
  });
}

export default process.env.EXPO_PUBLIC_SENTRY_DNS ? Sentry.wrap(RootLayout) : RootLayout;
